import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Zap, Upload, FileText, Copy, Download, X, Moon, Sun, CheckCircle, Info, RotateCcw, Files } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import ToolsNav from "@/components/ToolsNav";
import { RenderCMSContent } from "@/components/CMSRenderer";
import { AdContainer, ResponsiveAd, StickyMobileAd, DesktopOnlyAd } from "@/components/AdPlacement";

// Set the worker source for pdf.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.mjs`;

/**
 * Extract text from PDF with proper paragraph and line formatting
 * This analyzes text item positions to preserve the original layout
 */
async function extractFormattedText(page) {
  const textContent = await page.getTextContent();
  const viewport = page.getViewport({ scale: 1.0 });
  
  if (!textContent.items || textContent.items.length === 0) {
    return "";
  }
  
  // Sort items by Y position (top to bottom), then X position (left to right)
  const sortedItems = textContent.items
    .filter(item => item.str && item.str.trim())
    .map(item => {
      // Get the transform matrix [scaleX, skewX, skewY, scaleY, translateX, translateY]
      const tx = item.transform;
      return {
        str: item.str,
        x: tx[4],
        y: viewport.height - tx[5], // Convert to top-down coordinates
        width: item.width || 0,
        height: Math.abs(tx[0]) || 12, // Font size approximation
        fontName: item.fontName || ''
      };
    })
    .sort((a, b) => {
      // Sort by Y first (with tolerance for same line)
      const yDiff = a.y - b.y;
      if (Math.abs(yDiff) < 5) {
        return a.x - b.x; // Same line, sort by X
      }
      return yDiff;
    });
  
  if (sortedItems.length === 0) {
    return "";
  }
  
  // Group items into lines based on Y position
  const lines = [];
  let currentLine = [sortedItems[0]];
  let lastY = sortedItems[0].y;
  
  for (let i = 1; i < sortedItems.length; i++) {
    const item = sortedItems[i];
    const lineHeight = currentLine[0]?.height || 12;
    const yThreshold = lineHeight * 0.7; // Items within 70% of line height are same line
    
    if (Math.abs(item.y - lastY) < yThreshold) {
      // Same line
      currentLine.push(item);
    } else {
      // New line
      lines.push(currentLine);
      currentLine = [item];
      lastY = item.y;
    }
  }
  // Don't forget the last line
  if (currentLine.length > 0) {
    lines.push(currentLine);
  }
  
  // Build text with proper spacing
  const result = [];
  let lastLineY = 0;
  let lastLineHeight = 12;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Sort items in line by X position
    line.sort((a, b) => a.x - b.x);
    
    // Build line text with proper word spacing
    let lineText = "";
    let lastX = 0;
    let lastWidth = 0;
    
    for (let j = 0; j < line.length; j++) {
      const item = line[j];
      
      if (j > 0) {
        // Calculate gap between words
        const gap = item.x - (lastX + lastWidth);
        const avgCharWidth = lastWidth / Math.max(1, lineText.split('').length);
        
        // If gap is larger than average char width, add space
        if (gap > avgCharWidth * 0.3) {
          lineText += " ";
        }
      }
      
      lineText += item.str;
      lastX = item.x;
      lastWidth = item.width;
    }
    
    // Detect paragraph breaks (larger gaps between lines)
    if (i > 0 && lines[i - 1].length > 0) {
      const currentY = line[0]?.y || 0;
      const gap = currentY - lastLineY;
      const avgLineHeight = lastLineHeight;
      
      // If gap is significantly larger than line height, it's a new paragraph
      if (gap > avgLineHeight * 1.8) {
        result.push(""); // Add empty line for paragraph break
      }
    }
    
    result.push(lineText.trim());
    lastLineY = line[0]?.y || 0;
    lastLineHeight = line[0]?.height || 12;
  }
  
  return result.join("\n");
}

export default function PdfToText() {
  const [files, setFiles] = useState([]);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [extractedTexts, setExtractedTexts] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [copied, setCopied] = useState(false);
  const [cmsContent, setCmsContent] = useState(null);
  const [showPageNumbers, setShowPageNumbers] = useState(true);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  
  const fileInputRef = useRef(null);
  const addMoreInputRef = useRef(null);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Apply dark mode
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  // Fetch CMS content
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/pages/slug/pdf-to-text`);
        if (response.ok) {
          const data = await response.json();
          setCmsContent(data);
          
          if (data.seo) {
            if (data.seo.meta_title) document.title = data.seo.meta_title;
            if (data.seo.meta_description) {
              let meta = document.querySelector('meta[name="description"]');
              if (!meta) {
                meta = document.createElement('meta');
                meta.name = 'description';
                document.head.appendChild(meta);
              }
              meta.content = data.seo.meta_description;
            }
          }
        }
      } catch (error) {
        console.log('No CMS content found');
      }
    };
    fetchContent();
  }, [API_URL]);

  const extractTextFromPDF = async (pdfFile, fileId) => {
    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      setTotalPages(pdf.numPages);
      let fullText = "";
      
      for (let i = 1; i <= pdf.numPages; i++) {
        setCurrentPage(i);
        const page = await pdf.getPage(i);
        
        // Use enhanced extraction with paragraph detection
        const pageText = await extractFormattedText(page);
        
        if (showPageNumbers && pdf.numPages > 1) {
          fullText += `─────────────────────────────────────────\n`;
          fullText += `                    Page ${i}\n`;
          fullText += `─────────────────────────────────────────\n\n`;
        }
        
        fullText += pageText;
        
        if (i < pdf.numPages) {
          fullText += "\n\n";
        }
        
        setProgress(Math.round((i / pdf.numPages) * 100));
      }
      
      return {
        text: fullText.trim(),
        pageCount: pdf.numPages
      };
    } catch (error) {
      console.error("PDF extraction error:", error);
      throw error;
    }
  };

  const processFiles = async (newFiles) => {
    setIsProcessing(true);
    setProgress(0);
    
    const results = { ...extractedTexts };
    let processedCount = 0;
    
    for (const file of newFiles) {
      try {
        const result = await extractTextFromPDF(file, file.name);
        results[file.name] = {
          text: result.text,
          pageCount: result.pageCount,
          size: file.size
        };
        processedCount++;
      } catch (error) {
        toast.error(`Failed to extract text from ${file.name}`);
        results[file.name] = {
          text: `Error: Failed to extract text from this PDF. The file may be encrypted, corrupted, or contain only images.`,
          pageCount: 0,
          size: file.size,
          error: true
        };
      }
    }
    
    setExtractedTexts(results);
    setIsProcessing(false);
    setProgress(0);
    setCurrentPage(0);
    
    if (processedCount > 0) {
      toast.success(`Extracted text from ${processedCount} file${processedCount > 1 ? 's' : ''}!`);
    }
  };

  const handleAddMoreClick = () => {
    if (addMoreInputRef.current) {
      addMoreInputRef.current.click();
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []).filter(
      f => f.type === "application/pdf"
    );
    
    if (selectedFiles.length === 0) {
      toast.error("Please select valid PDF files");
      return;
    }
    
    setFiles(prev => [...prev, ...selectedFiles]);
    setCurrentFileIndex(0);
    processFiles(selectedFiles);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      f => f.type === "application/pdf"
    );
    
    if (droppedFiles.length === 0) {
      toast.error("Please drop valid PDF files");
      return;
    }
    
    setFiles(prev => [...prev, ...droppedFiles]);
    setCurrentFileIndex(0);
    processFiles(droppedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const currentFile = files[currentFileIndex];
  const currentResult = currentFile ? extractedTexts[currentFile.name] : null;
  const currentText = currentResult?.text || "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentText);
      setCopied(true);
      toast.success("Text copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy text");
    }
  };

  const handleCopyAll = async () => {
    try {
      const allText = Object.entries(extractedTexts)
        .map(([name, data]) => `=== ${name} ===\n\n${data.text}`)
        .join("\n\n" + "═".repeat(50) + "\n\n");
      
      await navigator.clipboard.writeText(allText);
      setCopied(true);
      toast.success("All text copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy text");
    }
  };

  const handleDownload = () => {
    const blob = new Blob([currentText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const fileName = currentFile?.name.replace(".pdf", "") || "extracted";
    link.download = `${fileName}.txt`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Text file downloaded!");
  };

  const handleDownloadAll = () => {
    const allText = Object.entries(extractedTexts)
      .map(([name, data]) => `=== ${name} ===\n\n${data.text}`)
      .join("\n\n" + "═".repeat(50) + "\n\n");
    
    const blob = new Blob([allText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `extracted_texts_${Date.now()}.txt`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success("All text files downloaded!");
  };

  const handleReset = () => {
    setFiles([]);
    setExtractedTexts({});
    setCurrentFileIndex(0);
    setTotalPages(0);
  };

  const handleRemoveFile = (index) => {
    const fileToRemove = files[index];
    const newFiles = files.filter((_, i) => i !== index);
    const newTexts = { ...extractedTexts };
    delete newTexts[fileToRemove.name];
    
    setFiles(newFiles);
    setExtractedTexts(newTexts);
    
    if (currentFileIndex >= newFiles.length) {
      setCurrentFileIndex(Math.max(0, newFiles.length - 1));
    }
  };

  const updateText = (newText) => {
    if (currentFile) {
      setExtractedTexts(prev => ({
        ...prev,
        [currentFile.name]: {
          ...prev[currentFile.name],
          text: newText
        }
      }));
    }
  };

  const wordCount = currentText.trim() ? currentText.trim().split(/\s+/).length : 0;
  const charCount = currentText.length;
  const paragraphCount = currentText.split(/\n\s*\n/).filter(p => p.trim()).length;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors duration-300">
      <Toaster position="top-center" theme={darkMode ? "dark" : "light"} />
      
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 md:py-6">
          <div className="flex items-center justify-between">
            <a href="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                KayTool
              </h1>
            </a>
            <div className="flex items-center gap-3">
              <ToolsNav />
              <Badge variant="secondary" className="hidden md:flex items-center gap-1 px-3 py-1 dark:bg-slate-700 dark:text-slate-200">
                <Info className="w-3 h-3" />
                100% Local Processing
              </Badge>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setDarkMode(prev => !prev)}
                className="rounded-full w-10 h-10 border-slate-200 dark:border-slate-600 dark:bg-slate-700"
              >
                {darkMode ? <Sun className="w-5 h-5 text-yellow-500" /> : <Moon className="w-5 h-5 text-slate-600" />}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Ad Placement - Above the Fold (Below Header) */}
      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8 md:py-12">
        {/* Title Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900/30 rounded-full mb-4">
            <FileText className="w-5 h-5 text-red-600 dark:text-red-400" />
            <span className="text-sm font-semibold text-red-700 dark:text-red-400">PDF to Text</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-4 tracking-tight text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Extract Text from PDF
          </h2>
          <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Convert PDF documents to plain text with preserved paragraphs and formatting. Upload multiple files at once.
          </p>
        </div>

        {/* Upload or Result */}
        {files.length === 0 ? (
          <div className="max-w-2xl mx-auto">
            <div
              data-testid="upload-zone"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`h-64 border-4 border-dashed rounded-xl cursor-pointer ${
                isDragging ? "border-red-500 bg-red-50 dark:bg-red-900/20" : "border-slate-300 dark:border-slate-600"
              } bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 flex flex-col items-center justify-center gap-4 transition-colors`}
            >
              <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Upload className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <div className="text-center">
                <p className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
                  Drop PDF files here or click to upload
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">Upload multiple PDF files at once</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
            </div>
          </div>
        ) : (
          <div className="max-w-5xl mx-auto space-y-6">
            {/* File Tabs */}
            {files.length > 1 && (
              <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2 overflow-x-auto pb-2">
                    <Files className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    {files.map((file, index) => (
                      <button
                        key={file.name}
                        onClick={() => setCurrentFileIndex(index)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                          index === currentFileIndex
                            ? 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300'
                            : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
                        }`}
                      >
                        <FileText className="w-3 h-3" />
                        {file.name.length > 20 ? file.name.substring(0, 17) + '...' : file.name}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFile(index);
                          }}
                          className="ml-1 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </button>
                    ))}
                    <button
                      onClick={handleAddMoreClick}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600 whitespace-nowrap"
                      data-testid="add-more-tab-button"
                    >
                      <Upload className="w-3 h-3" />
                      Add More
                    </button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* File Info & Actions */}
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                      <FileText className="w-6 h-6 text-red-600 dark:text-red-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-white truncate max-w-xs">
                        {currentFile?.name || 'No file selected'}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">
                        {currentFile && `${(currentFile.size / 1024).toFixed(1)} KB`}
                        {currentResult?.pageCount > 0 && ` • ${currentResult.pageCount} pages`}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {currentText && !currentResult?.error && (
                      <>
                        <Button
                          onClick={handleCopy}
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                          data-testid="copy-button"
                        >
                          {copied ? <CheckCircle className="w-4 h-4 mr-1 text-green-500" /> : <Copy className="w-4 h-4 mr-1" />}
                          {copied ? "Copied!" : "Copy"}
                        </Button>
                        <Button
                          onClick={handleDownload}
                          size="sm"
                          className="rounded-full bg-red-600 hover:bg-red-700"
                          data-testid="download-button"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download TXT
                        </Button>
                      </>
                    )}
                    {files.length > 1 && (
                      <>
                        <Button
                          onClick={handleCopyAll}
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Copy All
                        </Button>
                        <Button
                          onClick={handleDownloadAll}
                          variant="outline"
                          size="sm"
                          className="rounded-full"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download All
                        </Button>
                      </>
                    )}
                    <Button
                      onClick={handleReset}
                      variant="outline"
                      size="sm"
                      className="rounded-full border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400"
                      data-testid="reset-button"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Start Over
                    </Button>
                  </div>
                </div>

                {/* Progress */}
                {isProcessing && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        Extracting page {currentPage} of {totalPages}...
                      </span>
                      <span className="text-sm font-medium text-red-600">{progress}%</span>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Extracted Text */}
            {currentText && (
              <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-slate-900 dark:text-white">Extracted Text</h3>
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant="secondary" className="dark:bg-slate-700">
                        {paragraphCount.toLocaleString()} paragraphs
                      </Badge>
                      <Badge variant="secondary" className="dark:bg-slate-700">
                        {wordCount.toLocaleString()} words
                      </Badge>
                      <Badge variant="secondary" className="dark:bg-slate-700">
                        {charCount.toLocaleString()} chars
                      </Badge>
                    </div>
                  </div>
                  <Textarea
                    value={currentText}
                    onChange={(e) => updateText(e.target.value)}
                    className={`min-h-[500px] font-mono text-sm leading-relaxed bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 ${
                      currentResult?.error ? 'text-red-500' : ''
                    }`}
                    data-testid="extracted-text"
                  />
                </CardContent>
              </Card>
            )}

            {/* Add More Files */}
            {files.length === 1 && (
              <div
                onClick={handleAddMoreClick}
                className="h-24 border-2 border-dashed rounded-lg cursor-pointer border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center justify-center gap-2 transition-colors"
                data-testid="add-more-files-zone"
              >
                <Upload className="w-5 h-5 text-slate-400" />
                <span className="text-slate-500 dark:text-slate-400">Add more PDF files</span>
              </div>
            )}
          </div>
        )}

        {/* Features */}
        <div className="max-w-4xl mx-auto mt-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">100% Private</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Files processed locally, never uploaded.
                </p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Preserved Layout</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Keeps paragraphs and line breaks intact.
                </p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center mx-auto mb-4">
                  <Files className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Batch Processing</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Upload multiple PDFs at once.
                </p>
              </CardContent>
            </Card>
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <h3 className="font-semibold text-slate-900 dark:text-white mb-2">Fast & Free</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Extract text in seconds, no limits.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Hidden file input for additional uploads */}
        <input
          ref={addMoreInputRef}
          type="file"
          accept=".pdf,application/pdf"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          data-testid="add-more-file-input"
        />

        {/* Ad Placement - Before Features */}
        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      {/* CMS Content */}
      {cmsContent?.content && (
        <div className="bg-slate-50 dark:bg-slate-900 py-12">
          <div className="container mx-auto px-4">
            <RenderCMSContent content={cmsContent.content} />
          </div>
        </div>
      )}


      {/* Ad Placement - Before Footer */}
      <AdContainer className="my-6">
        <ResponsiveAd slot="before-footer" mobileSize="mobile-rectangle" desktopSize="rectangle" />
      </AdContainer>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      {/* Sticky Mobile Ad */}
      <StickyMobileAd slot="sticky-footer" />

    </div>
  );
}
